import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ModalSingleComponent } from "../../../../shared/components/modal-single/modal-single.component";
import { IPerformLog, PERFORM_LOG_ACTION_DISPLAY_DICT, PERFORM_LOG_ACTION_ENUM, PERFORM_LOG_SENTIMENT_OPTIONS, PERFORM_LOG_TYPE_DISPLAY_DICT, PERFORM_LOG_TYPE_ENUM, PerformLogBackend, PerformLogEvidence, PerformLogForm, PerformLogSentiment } from '../perform-log.type';
import { StaffLinkDisplayComponent } from "../../../../shared/components/staff-link-display/staff-link-display.component";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfirmationModal } from 'src/app/shared/utils/base-class/confirmation-modal';
import { UIStreamlineIconComponent } from "../../../../ui/streamline-icon/ui-streamline-icon.component";
import { NgbDateSelectorComponent } from "../../../../shared/components/ngb-date-selector/ngb-date-selector.component";
import { CKEditorComponent } from 'src/app/shared-modules/ckeditor4-angular/ckeditor.component';
import { EDITOR_CONFIG_SIMPLE } from 'src/config/ckeditor-config';
import { NgSelectModule } from '@ng-select/ng-select';
import { CustomisedMultiSelectForGuidelineslComponent } from "../../../../shared/components/customised-multi-select-for-guidelines/customised-multi-select-for-guidelines.component";
import { OrgStandardsSelectorComponent } from "../../../../shared/org-standards-selector/org-standards-selector.component";
import { CustomisedMultiSelectForOrgCapabilitiesComponent } from "../../../../shared/components/customised-multi-select-for-org-capabilities/customised-multi-select-for-org-capabilities.component";
import { toSignal } from '@angular/core/rxjs-interop';
import { ResourceService } from 'src/app/services/resource.service';
import { OrgFrameworksService } from 'src/app/services/org-frameworks.service';
import { map, switchMap } from 'rxjs/operators';
import { MatDivider } from '@angular/material/divider';
import { ToggleButtonsComponent } from "../../../../shared/components/toggle-buttons/toggle-buttons.component";
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { PerformLogEvidenceModalComponent } from './perform-log-evidence-modal/perform-log-evidence-modal.component';
import { PerformLogEvidenceCardComponent } from "./perform-log-evidence-card/perform-log-evidence-card.component";
import { UserService } from 'src/app/services/user.service';
import { SelectorSingleUserComponent } from "../../../../shared/components/common-selectors/selector-single-user/selector-single-user.component";
import { PerformLogService } from '../perform-log.service';
import { NgIf } from '@angular/common';
import { ProcessingButtonComponent } from "../../../../ui/buttons/processing-button/processing-button.component";
import { ToastrService } from 'ngx-toastr';
import { combineLatest, of } from 'rxjs';
import { difference, intersection } from 'lodash';
import { HierarchyService } from 'src/app/pages/admin/hierarchy/hierarchy.service';
import { AnalyticsService } from 'src/app/services/analytics.service';


@Component({
  selector: 'app-add-edit-perform-log-modal',
  imports: [ModalSingleComponent, StaffLinkDisplayComponent, NgSelectModule, MatDivider, NgIf,
    UIStreamlineIconComponent, NgbDateSelectorComponent, CKEditorComponent, ReactiveFormsModule, CustomisedMultiSelectForGuidelineslComponent, NgbTooltip,
    OrgStandardsSelectorComponent, CustomisedMultiSelectForOrgCapabilitiesComponent, ToggleButtonsComponent, PerformLogEvidenceCardComponent, SelectorSingleUserComponent, ProcessingButtonComponent],
  templateUrl: './add-edit-perform-log-modal.component.html',
  styleUrl: './add-edit-perform-log-modal.component.scss'
})
export class AddEditPerformLogModalComponent extends ConfirmationModal implements OnInit {
    resourceService = inject(ResourceService);
    performLogService = inject(PerformLogService);
    orgFrameworksService = inject(OrgFrameworksService);
    toaster = inject(ToastrService);
    userService = inject(UserService);
    ngbModal = inject(NgbModal);
    hierarchyService = inject(HierarchyService);
    analyticsService = inject(AnalyticsService);
    sentimentOptions = PERFORM_LOG_SENTIMENT_OPTIONS;
    isSubmitting = signal(false);
    originalEvidences: PerformLogEvidence[];
    isEditModal = computed(() => (!!this.editedLog()));



    editedLog= signal<IPerformLog>(null);
    submitAttempted = signal(false);

    guidelines = toSignal(this.resourceService.fetchCachedGuidelines().pipe(map((guidelines) => this.resourceService.getEnabledGuidelines(guidelines))));
    enabledOrgCapabilities = toSignal(this.orgFrameworksService.fetchCachedOrgCapabilityFrameworks().pipe(map((orgCapabilityFrameworks) => orgCapabilityFrameworks.filter(frameworks => frameworks.orgStandards).map(f => f.orgStandards).flat())));


    EDITOR_CONFIG = EDITOR_CONFIG_SIMPLE;
    typeItems = Object.keys(PERFORM_LOG_TYPE_DISPLAY_DICT).map(k => {
        return { label: PERFORM_LOG_TYPE_DISPLAY_DICT[k], value: k };
    });
    actionItems = Object.keys(PERFORM_LOG_ACTION_DISPLAY_DICT).map(k => {
        return { label: PERFORM_LOG_ACTION_DISPLAY_DICT[k], value: k };
    });

    heading = computed(() => {
      if (this.editedLog()) {
        return 'Edit Perform Note';
      } else {
        return 'Add Perform Note';
      }
    });
    logForm = new FormGroup({
      // user: new FormControl<string>(null, Validators.required),
      staffOrgUserID: new FormControl<string>(null, Validators.required),
      occurDate: new FormControl<string>(null, Validators.required),
      note: new FormControl<string>(null, Validators.required),
      type: new FormControl<PERFORM_LOG_TYPE_ENUM>(PERFORM_LOG_TYPE_ENUM.attitudeOrBehaviour),
      action: new FormControl<PERFORM_LOG_ACTION_ENUM>(PERFORM_LOG_ACTION_ENUM.requestToMeet),
      guidelines: new FormControl<string[]>([]),
      orgStandards: new FormControl<string[]>([]),
      orgCapabilities: new FormControl<string[]>([]),
      evidences: new FormControl<PerformLogEvidence[]>([]),
      sentiment: new FormControl<PerformLogSentiment>('Neutral'),
    });

    ngOnInit(): void {
      if(this.isEditModal()){
        this.initEditModal();
      }
    }

    initEditModal(){
      this.originalEvidences = this.performLogService.convertBackendNotesToFnNotes(this.editedLog().notes);
      this.logForm.setValue({
        staffOrgUserID: this.userService.allOrgUserDictionaryByUserID[this.editedLog().staffUserID].orgUserID,
        occurDate: this.editedLog().occurDate,
        note: this.editedLog().note,
        type: this.editedLog().category,
        action: this.editedLog().action,
        guidelines: this.editedLog().standards || [],
        evidences: this.originalEvidences || [],
        orgStandards: this.editedLog().orgStandards?.filter(s => this.orgFrameworksService.isOrgStandard(s)) || [],
        orgCapabilities: this.editedLog().orgStandards?.filter(s => this.orgFrameworksService.isOrgCapability(s)) || [],
        sentiment: this.editedLog().sentiment,
      });
    }

    getEvidencesChanges(){
      const originalEvidencesIDs = this.originalEvidences?.map(e => e.noteID);
      const currentEvidencesIDs = this.logForm.get('evidences').value?.map(e => e.noteID);
      const sameEvidencesIDs = intersection(originalEvidencesIDs, currentEvidencesIDs);
      const newEvidenceIDs = difference(currentEvidencesIDs, sameEvidencesIDs);
      const deletedEvidenceIDs = difference(originalEvidencesIDs, sameEvidencesIDs);

      return {
        newEvidences: this.logForm.get('evidences').value?.filter(e => newEvidenceIDs.includes(e.noteID)),
        deletedEvidences: this.originalEvidences?.filter(e => deletedEvidenceIDs.includes(e.noteID)),
      }
    }



    submit(){
      this.isSubmitting.set(true);
      const allOrgStandards = this.logForm.get('orgStandards')?.value?.concat(this.logForm.get('orgCapabilities').value);
      const payload: PerformLogForm = {
        orgID: localStorage.getItem('orgID'),
        orgUserID: this.logForm.get('staffOrgUserID').value,
        staffUserID: this.userService.managedOrgUserDictionaryByOrgUserID[this.logForm.get('staffOrgUserID').value].userID,
        managerID: this.hierarchyService.getDirectManagerByOrgUserID(this.logForm.get('staffOrgUserID').value)?.userID,
        category: this.logForm.get('type').value,
        action: this.logForm.get('action').value,
        note: this.logForm.get('note').value,
        occurDate: this.logForm.get('occurDate').value,
        // should not be a empty array []. Otherwise it will trigger a error when fetch the all perform log
        standards: this.logForm.get('guidelines')?.value?.length ? this.logForm.get('guidelines').value : null,
        orgStandards: allOrgStandards?.length ? allOrgStandards : null,
        sentiment: this.logForm.get('sentiment').value,
      }


      if(this.isEditModal()){
        const {newEvidences, deletedEvidences} = this.getEvidencesChanges();
        this.performLogService.updatePerformLog(this.editedLog().logID, payload).pipe(
          // add new evidences
          switchMap(
            (log: PerformLogBackend) => {
              this.analyticsService.track("PR-performnote-update", {
                logID: log.logID,
                updatedBy: log.updatedBy,
              })
              const notes = newEvidences?.map(evidence => ({
                note: evidence?.description,
                links: [{
                  title: evidence?.title,
                  link: evidence?.link,
                }]
              }));
              const requests$ = notes?.map(note => this.performLogService.createPerformLogNote(log.logID, note));
              return newEvidences?.length ? combineLatest(requests$) : of([log]);
            }
          ),
          // remove deleted evidences
          switchMap(
            (logs) => {
              const deletedEvidenceIDs = deletedEvidences?.map(e => e?.noteID);
              const requests$ = deletedEvidenceIDs?.map(noteID => this.performLogService.deletePerformLogNote(this.editedLog()?.logID, noteID));
              return deletedEvidences?.length ? combineLatest(requests$) : of(logs);
            }
          ),
        ).subscribe(
          (updateLogs: PerformLogBackend[]) => {
            console.log('updateLogs',updateLogs)
            this.performLogService.updateLogInCacher(updateLogs[updateLogs.length - 1]);
            this.isSubmitting.set(false);
            this.activeModal.close();
            this.toaster.success(`Performance log edited`);
          }
        );
      } else {
        this.performLogService.createPerformLog(payload).pipe(
          switchMap(
            (log: PerformLogBackend) => {
              this.analyticsService.track("PR-performnote-added", {
                logID: log.logID,
                staffUserID: log.staffUserID,
                createdBy: log.createdBy,
              });
              const notes = this.logForm.get('evidences')?.value.map(evidence => ({
                note: evidence?.description,
                links: [{
                  title: evidence?.title,
                  link: evidence?.link,
                }]
              }));
              const requests$ = notes?.map(note => this.performLogService.createPerformLogNote(log?.logID, note));
              return notes?.length? combineLatest(requests$): of([log]);
            }
          )
        )
        .subscribe(
          (newLogs: PerformLogBackend[]) => {
            console.log('newLogs',newLogs)
            this.performLogService.addNewLogIntoCacher(newLogs[newLogs.length - 1]);
            this.isSubmitting.set(false);
            this.activeModal.close();
            this.toaster.success(`Performance log added`);
          }
        )
      }

    }

    addEvidence() {
      const activeModal = this.modalService.open(PerformLogEvidenceModalComponent, { size: 'md', backdrop: 'static' });
      // activeModal.componentInstance.editedEvidenceForm = this.logForm.get('evidences');
      activeModal.result.then(
        (evidenceForm: PerformLogEvidence) => {
          if(evidenceForm){
            const currentEvidences = this.logForm.get('evidences')?.value || [];
            this.logForm.get('evidences')?.setValue([...currentEvidences, evidenceForm]);
          }
        }
      );
    }

    deleteEvidence(index: number){
      const evidences = this.logForm.get('evidences')?.value || [];
      const updatedEvidences = evidences.filter((_, i) => i !== index);
      this.logForm.get('evidences')?.setValue(updatedEvidences);
    }

    showErrorMsg(formItem: string){
      const hasBeenTouched = this.logForm.get(formItem).touched || this.logForm.get(formItem).dirty;
      const isRequiredInvalid = this.logForm.get(formItem).errors?.['required'];
      return hasBeenTouched && isRequiredInvalid;
    }

    get evidences() {
      return this.logForm.get('evidences').value;
    }


}
